import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Detecta que o build publicado mudou enquanto a aba já estava aberta (deploy em
 * andamento) e avisa o usuário — sem isso, o JS antigo continua rodando até um F5
 * manual. Os buildspecs gravam dist/version.json com cache-control no-store, então o
 * CloudFront nunca segura uma versão velha desse arquivo (ver scripts/write-version.js).
 *
 * Padrão de polling + visibilitychange espelha AuthService.startHeartbeat/stopHeartbeat.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly CHECK_INTERVAL_MS = 5 * 60 * 1000;
  private readonly VISIBILITY_THROTTLE_MS = 60 * 1000;

  private baseline: string | null = null;
  private notified = false;
  private lastVisibilityCheckAt = 0;
  private checkTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(
    private messageService: MessageService,
    private translocoService: TranslocoService,
  ) {}

  /**
   * Chamado uma vez no boot (AppComponent.ngOnInit). Sem version.json (404/erro — ex.:
   * `ng serve` local, que não gera o arquivo) desliga silenciosamente: nunca agenda
   * polling nem o listener de visibilitychange.
   */
  public start(): void {
    if (this.checkTimer !== null) return; // já iniciado

    this.fetchVersion().then(version => {
      if (!version) return;
      this.baseline = version;

      this.checkTimer = setInterval(() => this.checkForUpdate(), this.CHECK_INTERVAL_MS);
      this.visibilityHandler = () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - this.lastVisibilityCheckAt < this.VISIBILITY_THROTTLE_MS) return;
        this.lastVisibilityCheckAt = now;
        this.checkForUpdate();
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    });
  }

  /** Desliga o polling e remove o listener — usado pelos testes para não vazar entre specs. */
  public stop(): void {
    if (this.checkTimer !== null) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
    if (this.visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private async checkForUpdate(): Promise<void> {
    if (this.notified) return;
    const version = await this.fetchVersion();
    if (version && version !== this.baseline) {
      this.notified = true;
      this.messageService.add({
        key: 'appUpdate',
        severity: 'info',
        summary: this.translocoService.translate('appUpdate.available'),
        detail: this.translocoService.translate('appUpdate.detail'),
        sticky: true,
        closable: false, // o template do toast tem o próprio botão de fechar
      });
    }
  }

  private async fetchVersion(): Promise<string | null> {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      return typeof data?.version === 'string' ? data.version : null;
    } catch {
      return null;
    }
  }
}
